import { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, ShieldCheck, Zap, Mail } from "lucide-react";
import { Reveal } from "@/components/Motion";

const GOLD = "#C9A84C";
const STRIPE_LINK = "https://buy.stripe.com/8x25kDbNC7fggry2VN0sU0b";

const KIT_ITEMS = [
  {
    n: "01",
    title: "AVANT QUE TU NE DEVIENNES",
    subtitle: "Le livre fondateur",
    desc: "Le manifeste de la méthode Triple Fusion™ — l'ouvrage qui pose les fondations de votre reconquête souveraine.",
  },
  {
    n: "02",
    title: "Les 12 Décrets Souverains",
    subtitle: "Les lois d'alignement",
    desc: "Les 12 principes non négociables qui structurent une vie souveraine — à imprimer, à afficher, à incarner.",
  },
  {
    n: "03",
    title: "Le Carnet de Travail Souverain",
    subtitle: "Votre journal opérationnel",
    desc: "Un carnet guidé jour par jour pour transformer les décrets en pratique quotidienne. 90 jours d'ancrage.",
  },
  {
    n: "04",
    title: "La Grille Triple Fusion™ Guidée",
    subtitle: "Votre cartographie personnelle",
    desc: "L'outil de synthèse pour lire votre profil Pythagore + Lo Shu + IFÀ d'un seul coup d'œil — modèle éditable.",
  },
  {
    n: "05",
    title: "La Carte des Directions Lo Shu",
    subtitle: "Votre boussole d'espace",
    desc: "La carte imprimable des 9 secteurs Lo Shu et de leurs directions — à poser près de votre poste de travail.",
  },
  {
    n: "06",
    title: "La Lettre du Fondateur",
    subtitle: "Le message de Rodrigue CASSILDÉ",
    desc: "Une lettre personnelle. La transmission directe du Fondateur aux nouveaux Souverains de l'Académie.",
  },
];

const PayButton = ({ children, primary = true, testId }) => (
  <a
    href={STRIPE_LINK}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className={`inline-flex items-center gap-3 px-10 py-5 text-sm md:text-base uppercase tracking-[0.15em] font-semibold transition-transform hover:scale-[1.03] ${
      primary ? "" : "border"
    }`}
    style={
      primary
        ? { backgroundColor: GOLD, color: "#0a0a0a" }
        : { borderColor: GOLD, color: GOLD, background: "rgba(201,168,76,0.06)" }
    }
  >
    {children} <ArrowRight size={18} />
  </a>
);

export default function KitDigital() {
  useEffect(() => {
    document.title = "Kit Digital Premium — Triple Fusion™ | Académie AETHERIS";
    const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
    meta.setAttribute("name", "description");
    meta.setAttribute("content", "Le Kit Digital Premium AETHERIS — 6 outils exclusifs pour décoder votre code vibratoire selon la méthode Triple Fusion™. 67 € · livraison immédiate.");
    document.head.appendChild(meta);
  }, []);

  return (
    <div className="min-h-screen bg-brand-ink text-white pt-24 pb-24" data-testid="kit-digital-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">

        {/* HERO */}
        <motion.header
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <p className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: GOLD }}>
            Kit Digital Premium — Académie AETHERIS
          </p>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95]">
            Kit Digital Premium<br />
            <span style={{ color: GOLD }}>Triple Fusion™</span>
          </h1>
          <p className="text-white/60 text-lg md:text-xl font-light mt-8 max-w-2xl mx-auto leading-relaxed">
            6 outils exclusifs pour décoder votre code vibratoire et engager votre transformation souveraine —
            selon la méthode Triple Fusion™ (Pythagore · Lo Shu · IFÀ).
          </p>

          <div className="mt-12 flex flex-col items-center gap-4" data-testid="hero-cta">
            <div className="flex items-baseline gap-3">
              <span className="font-serif text-6xl md:text-7xl" style={{ color: GOLD }}>67 €</span>
              <span className="text-white/40 text-sm uppercase tracking-luxe">paiement unique</span>
            </div>
            <PayButton testId="hero-buy">Je commande mon Kit — 67 €</PayButton>
            <p className="text-white/40 text-xs mt-2 flex items-center gap-2">
              <Mail size={12} /> Livraison par email immédiatement après paiement
            </p>
          </div>
        </motion.header>

        {/* PROMESSES */}
        <Reveal className="grid md:grid-cols-3 gap-4 mb-20">
          {[
            { icon: <Zap size={18} />, t: "Immédiat", d: "Vos 6 PDF sont livrés par email dans la minute qui suit le paiement." },
            { icon: <ShieldCheck size={18} />, t: "Paiement sécurisé", d: "Stripe — carte, Apple Pay, Google Pay. Aucun engagement." },
            { icon: <Check size={18} />, t: "À vie", d: "Vos outils vous appartiennent. Téléchargez-les et gardez-les à jamais." },
          ].map((b) => (
            <div key={b.t} className="p-6 border border-white/10 bg-brand-surface/30">
              <div className="mb-3" style={{ color: GOLD }}>{b.icon}</div>
              <p className="font-serif text-lg mb-2">{b.t}</p>
              <p className="text-white/60 text-sm leading-relaxed">{b.d}</p>
            </div>
          ))}
        </Reveal>

        {/* CE QUE VOUS RECEVEZ */}
        <section className="mb-24" data-testid="kit-items">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Ce que vous allez recevoir</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-16">
              6 outils souverains,<br />
              <span style={{ color: GOLD }}>conçus pour être utilisés.</span>
            </h2>
          </Reveal>

          <div className="space-y-4">
            {KIT_ITEMS.map((item, i) => (
              <motion.div
                key={item.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="p-6 md:p-8 border border-white/10 bg-brand-surface/30 hover:bg-brand-surface/50 transition-colors grid md:grid-cols-12 gap-6 items-center"
                data-testid={`kit-item-${item.n}`}
              >
                <div className="md:col-span-1">
                  <div className="font-serif text-3xl" style={{ color: GOLD }}>{item.n}</div>
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-serif text-xl md:text-2xl">{item.title}</h3>
                  <p className="text-xs uppercase tracking-luxe text-white/40 mt-1">{item.subtitle}</p>
                </div>
                <div className="md:col-span-7">
                  <p className="text-white/70 font-light leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FOCUS LIVRE — Avant que tu ne deviennes */}
        <section className="mb-24" data-testid="book-focus">
          <Reveal>
            <div
              className="p-8 md:p-14 border relative overflow-hidden"
              style={{ borderColor: `${GOLD}55`, background: "linear-gradient(135deg, rgba(201,168,76,0.06) 0%, rgba(0,0,0,0.4) 100%)" }}
            >
              <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>
                📖 Le livre inclus dans le Kit
              </p>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-6">
                Avant que tu ne deviennes<br />
                <span style={{ color: GOLD }}>— Le livre que tu aurais dû recevoir à ta naissance.</span>
              </h2>

              <div className="space-y-5 text-white/70 font-light leading-relaxed max-w-3xl">
                <p>Il y a un livre qu'on aurait dû te remettre le jour de ta naissance.</p>
                <p>
                  Pas un livre de développement personnel de plus. Pas une méthode à appliquer le lundi
                  et oublier le jeudi.
                </p>
                <p className="text-xl md:text-2xl font-serif" style={{ color: GOLD }}>
                  Un décodeur. De toi.
                </p>
                <p>
                  Quelque chose qui t'aurait dit : voilà ce que tu es vraiment. Voilà les cycles
                  qui vont traverser ta vie. Voilà pourquoi tu portes des schémas qui ne t'appartiennent pas.
                </p>
                <p className="text-white italic">
                  Ce livre existe. Il a juste quelques années de retard.
                </p>
              </div>

              <div className="mt-12">
                <p className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: GOLD }}>
                  Ce que tu découvres dans ses pages
                </p>
                <ul className="space-y-4 text-white/70 font-light">
                  {[
                    "Pourquoi tu répètes certains schémas — et d'où ils viennent réellement (la réponse n'est pas là où tu crois).",
                    "Comment les cycles numériques ont façonné « qui tu es devenu » sans que tu le choisisses.",
                    "Le verrou de lignée qui sabote silencieusement tes décisions depuis des années.",
                    "La distinction entre ce qui est TOI et ce qui a été imposé — et comment agir depuis cet endroit-là.",
                    "La méthode Triple Fusion™ appliquée à la (re)construction identitaire.",
                  ].map((line) => (
                    <li key={line} className="flex gap-4 items-start">
                      <span className="font-serif text-lg leading-none pt-1" style={{ color: GOLD }}>◆</span>
                      <span className="flex-1 leading-relaxed">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div
                className="mt-12 pt-10 border-t"
                style={{ borderColor: `${GOLD}33` }}
              >
                <p className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: GOLD }}>
                  Ce livre n'est pas pour tout le monde
                </p>
                <div className="space-y-4 text-white/70 font-light max-w-3xl">
                  <p>
                    Il est pour toi si tu as déjà ressenti :{" "}
                    <span className="text-white italic">
                      « Je réussis, mais je ne me reconnais pas dans ce que je construis. »
                    </span>
                  </p>
                  <p>Il est pour toi si tu sens qu'une partie de toi n'a jamais eu la permission d'exister vraiment.</p>
                  <p>Il est pour toi si tu es prêt·e à savoir — pas juste à espérer.</p>
                </div>
              </div>

              <p className="mt-12 text-xs uppercase tracking-[0.25em] text-white/40">
                Par Rodrigue CASSILDÉ — Fondateur de l'Académie AETHERIS, architecte de la méthode Triple Fusion™
              </p>
            </div>
          </Reveal>
        </section>

        {/* CTA FINAL */}
        <Reveal>
          <div className="text-center px-6 py-16 border" style={{ borderColor: `${GOLD}55`, background: "rgba(0,0,0,0.4)" }}>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Passer à l'action</p>
            <h2 className="font-serif text-4xl md:text-5xl mb-6 leading-tight">
              Votre reconquête<br />
              <span style={{ color: GOLD }}>commence maintenant.</span>
            </h2>
            <p className="text-white/60 text-lg font-light max-w-xl mx-auto mb-10 leading-relaxed">
              Un investissement unique de 67 € pour recevoir les 6 outils fondateurs de la méthode Triple Fusion™.
            </p>
            <PayButton testId="footer-buy">Je commande mon Kit — 67 €</PayButton>
            <p className="text-white/40 text-xs mt-6 flex items-center justify-center gap-2">
              <Mail size={12} /> Livraison par email immédiatement après paiement
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
